import { Injectable } from '@angular/core';
import { mapHelper } from '@helpers';
import { GeoJsonItem } from '@mzima-client/sdk';
import * as L from 'leaflet';
import { GeoJsonTransformerService } from './geojson-transformer.service';

export interface LayerCreationOptions {
  posts: GeoJsonItem[];
  onEachFeature?: (feature: any, layer: L.Layer) => void;
  clustering?: boolean;
  clusterOptions?: L.MarkerClusterGroupOptions;
}

@Injectable()
export class MapLayerService {
  constructor(private geoJsonTransformerService: GeoJsonTransformerService) {}

  /**
   * Create Leaflet layer or cluster group from post data
   */
  public createLayerFromPosts(options: LayerCreationOptions): L.Layer {
    const { posts, onEachFeature, clustering, clusterOptions } = options;

    const oldGeoJson: any = this.geoJsonTransformerService.transformPostsToGeoJson(posts);

    const geoPosts = L.geoJSON(oldGeoJson, {
      pointToLayer: mapHelper.pointToLayer,
      onEachFeature,
    });

    if (clustering && clusterOptions) {
      const clusterGroup = new L.MarkerClusterGroup(clusterOptions);
      clusterGroup.addLayer(geoPosts);
      return clusterGroup;
    }

    return geoPosts;
  }

  /**
   * Remove all layers from a map
   */
  public removeLayersFromMap(map: L.Map, layers: L.Layer[]): void {
    layers.forEach((layer) => {
      map.removeLayer(layer);

      if (layer instanceof L.MarkerClusterGroup) {
        layer.clearLayers();
      }
    });
  }

  /**
   * Add layer to map
   */
  public addLayerToMap(map: L.Map, layer: L.Layer): void {
    layer.addTo(map);
  }

  /**
   * Get bounds from GeoJSON layer
   */
  public getLayerBounds(layer: L.Layer): L.LatLngBounds | null {
    if (layer instanceof L.GeoJSON || layer instanceof L.FeatureGroup) {
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        return bounds;
      }
    }
    return null;
  }

  /**
   * Count total markers in layer (including clustered markers)
   */
  public countMarkersInLayer(layer: L.Layer): number {
    if (layer instanceof L.MarkerClusterGroup) {
      return layer.getLayers().length;
    }
    if (layer instanceof L.FeatureGroup || layer instanceof L.GeoJSON) {
      return layer.getLayers().length;
    }
    return 0;
  }
}
